import {
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import { NvButton } from '../../ui/button/nv-button';
import { NvIcon } from '../../ui/icon/nv-icon';
import { NvToastService } from '../../ui/toast/nv-toast';
import { NvDialogService } from '../../ui/dialog/nv-dialog.service';
import { PermissionService } from '../../../core/permissions/permission.service';
import {
  RewardPinResetDialog,
  RewardPinResetDialogData,
} from '../reward-pin-reset-dialog/reward-pin-reset-dialog';
import { PendingRequestsService } from '../../../core/notifications/pending-requests.service';
import { AlarmService } from '../../../core/notifications/alarm.service';
import { SpeechService } from '../../../core/notifications/speech.service';
import { TenancyService } from '../../../core/tenancy/tenancy.service';
import { EntitlementService } from '../../../core/tenancy/entitlement.service';
import { FloorStore } from '../../../core/stores/floor.store';
import { VenueTable } from '../../models/table.model';
import { GuestRequest } from '../../models/guest-request.model';
import {
  REQUEST_REASON_ICON,
  REQUEST_REASON_LABEL,
  REQUEST_REASON_SPEECH,
} from '../../models/guest-request-labels';
import { toMillis } from '../../utils/timestamp.util';
import { REQUEST_OVERDUE_MS } from '../../constants/request.constants';

/**
 * How many arrivals in one batch get announced by name before the rest are
 * summarised. A reload with a backlog of waiting tables would otherwise
 * queue a minute of speech before the first useful word.
 */
const MAX_SPOKEN_PER_BATCH = 3;

/** How often an unaddressed request's alert repeats — measured from the oldest still-pending request's own age, not from when the tab happened to load. */
const ROUND_MS = 60_000;
/** Client spec (Sep 2026): alert on arrival, remind at 2 and 3 min (bounded tune+speech+fade cycles), then from the 4th minute go continuous for managers and admins. Round 1 is deliberately silent. */
const CONTINUOUS_AT_ROUND = 4;
const SILENT_ROUNDS = new Set([1]);
/** Once continuous, the spoken line repeats on its own 30-second cadence — decoupled from ROUND_MS, which would otherwise leave an admin hearing nothing but the tune for up to a minute at a time during the most urgent phase. */
const CONTINUOUS_SPEECH_MS = 30_000;
/** Fine enough that a round boundary is never more than this late, coarse enough not to burn a timer for nothing. */
const TICK_MS = 5_000;

/**
 * Persistent alert strip: which tables are waiting, for what, and for how
 * long — with a one-tap acknowledge.
 *
 * It lives in the app shell rather than on one page because the point is
 * that staff see it wherever they happen to be.
 *
 * Alerting escalates in rounds keyed off the *oldest* still-pending
 * request's age (acknowledging it hands the round timer to whichever
 * request is oldest next, rather than resetting on every acknowledgement):
 *   - Round 0 (on arrival) and round 1/2 (unaddressed at 1 and 2 minutes):
 *     a bounded cycle — AlarmService.playRound() starts the tune, the
 *     round's announcement is spoken over it, and once speech finishes the
 *     tune fades out over three seconds (AlarmService.endRound()).
 *   - Round 3+ (three minutes unaddressed): if the *current viewer* is the
 *     venue's owner, the tune switches to AlarmService's continuous mode
 *     instead of another bounded cycle — a warning easy to miss as one more
 *     three-second chime is not meant to be ignorable. A non-admin viewer
 *     keeps getting the ordinary bounded repeat every minute instead; they
 *     are not who "owner login... as warning" was written for.
 *   - Whatever is playing — a round mid-fade or the continuous alarm —
 *     winds down (fade, never a hard cut) the instant nothing is pending
 *     any more, i.e. the moment the last outstanding request is
 *     acknowledged from the portal.
 *
 * Every trigger (a new arrival, or a round boundary) runs through kick()/
 * loop(), a small re-entrant-safe queue: if one is already mid-flight when
 * another fires, it just marks the run `dirty` so the in-flight one loops
 * again on completion, rather than overlapping two tune fades.
 */
@Component({
  selector: 'nv-request-alerts',
  imports: [NvButton, NvIcon],
  templateUrl: './request-alerts.html',
  styleUrl: './request-alerts.scss',
})
export class RequestAlerts implements OnDestroy {
  private readonly requests = inject(PendingRequestsService);
  private readonly alarm = inject(AlarmService);
  private readonly speech = inject(SpeechService);
  private readonly tenancy = inject(TenancyService);
  private readonly entitlements = inject(EntitlementService);
  private readonly floor = inject(FloorStore);
  private readonly toasts = inject(NvToastService);
  private readonly dialog = inject(NvDialogService);
  private readonly permissions = inject(PermissionService);

  /**
   * Repointed onto FloorStore (Workspace Mock migration, Phase 2) — was one
   * of 7 independent TableService.list() call sites; FloorStore already owns
   * the same licenseId-guard this used to build inline (TableService throws
   * when there is no license yet, and the shell renders before tenancy has
   * hydrated), so nothing about that safety property changed, only where the
   * listener lives.
   */
  private readonly allTables = this.floor.tables;

  readonly pending = this.requests.ordered;
  /** QA round 2, item I.1 — acknowledged-but-not-done requests, rendered separately below with a "Mark done" action; never part of the alarm/escalation reads above. */
  readonly acknowledgedRequests = this.requests.acknowledgedOrdered;
  readonly markingDone = signal<string | null>(null);

  /**
   * Item 11 — "Add water" ultimately calls createStaffOrder, which the
   * server gates on manage_orders (assertMayManageOrders). This dock is
   * mounted for every signed-in staff member with no permission gate of its
   * own, so without this check a staff member who can acknowledge requests
   * but lacks manage_orders would tap "Add water", have the server reject
   * it, and see only the generic "Could not add water to the order." — a
   * real, reachable dead end. Gated in the UI so that staff member instead
   * sees the plain Acknowledge action water already falls back to when no
   * water menu item exists.
   */
  readonly canAddWater = computed(() => this.tenancy.permissions().includes('manage_orders'));
  /** Rewards-PIN reset — a manager verifies the guest in person, then sets a new PIN. Same permission the rewards settings page requires. */
  readonly canSetPin = computed(() => this.permissions.can('manage_rewards'));
  readonly count = this.requests.count;
  readonly acknowledging = signal<string | null>(null);

  private readonly now = signal(Date.now());
  private readonly tickTimer: ReturnType<typeof setInterval>;
  /** Requests already seen, so a re-render doesn't read as a fresh arrival. */
  private seen = new Set<string>();
  /** Requests already spoken as a *new arrival*. Cleared to empty whenever
   * everything is acknowledged; repeat-round announcements don't consult
   * this at all, since "still waiting" is meant to be said again. */
  private announcedFor = new Set<string>();

  /** The last round number a cycle was actually fired for — so a re-render mid-round doesn't restart it, only an actual round advance (or a new arrival) does. */
  private lastFiredRound = -1;
  private continuousActive = false;
  /** When continuous mode last spoke — its own CONTINUOUS_SPEECH_MS cadence, independent of ROUND_MS. */
  private continuousSpeechAt = 0;
  private busy = false;
  private dirty = false;

  /** A background/inactive tab gets its setInterval throttled by the browser (Chrome: as coarse as once a minute), so a manager who tabs away for a few minutes can genuinely miss a round or two of the tick above. Forcing an immediate re-check on return means the round number is never more stale than however long the tab was actually hidden. */
  private readonly onVisible = (): void => {
    if (document.visibilityState === 'visible') this.now.set(Date.now());
  };

  constructor() {
    // Coarse enough not to be wasteful, fine enough that a round's own
    // 60-second boundary is never more than a few seconds late.
    this.tickTimer = setInterval(() => this.now.set(Date.now()), TICK_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisible);
    }

    effect(() => {
      // Read every signal this decision depends on before the (possibly
      // async) kick() — effects only re-run when a *read* dependency
      // changes, and kick() itself reads nothing reactively.
      this.pending();
      this.now();
      this.kick();
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.tickTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisible);
    }
    this.alarm.stop();
    this.alarm.endRound();
    this.speech.cancel();
  }

  readonly hasOverdue = computed(() => this.pending().some((request) => this.isOverdue(request)));

  tableLabel(request: GuestRequest): string {
    const table = this.allTables().find((candidate) => candidate.id === request.tableId);
    return table ? `Table ${table.number}` : 'Table';
  }

  label(request: GuestRequest): string {
    return REQUEST_REASON_LABEL[request.reason] ?? REQUEST_REASON_LABEL.custom;
  }

  icon(request: GuestRequest): string {
    return REQUEST_REASON_ICON[request.reason] ?? REQUEST_REASON_ICON.custom;
  }

  waitingFor(request: GuestRequest): string {
    const startedAt = toMillis(request.createdAt);
    if (!startedAt) return 'just now';
    const seconds = Math.max(0, Math.floor((this.now() - startedAt) / 1000));
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  }

  isOverdue(request: GuestRequest): boolean {
    const startedAt = toMillis(request.createdAt);
    return !!startedAt && this.now() - startedAt > REQUEST_OVERDUE_MS;
  }

  /**
   * One tap closes the request out entirely — acknowledge AND resolve.
   *
   * This dock used to mirror the full two-step lifecycle: Acknowledge here,
   * then the request dropped into a second "acknowledged" list below with
   * its own Mark done. On a floor dock whose whole job is to be cleared at
   * a glance, that meant every request had to be tapped twice, in two
   * different places, to disappear — and in practice the second list just
   * accumulated. The guest-facing distinction still exists (their chat
   * shows "someone is on their way" the moment acknowledge lands, which is
   * why this still acknowledges first rather than only resolving), it just
   * isn't two separate jobs for staff any more. Table Requests
   * (table-requests-list) still exposes both steps for anyone who wants
   * them.
   */
  async acknowledge(request: GuestRequest): Promise<void> {
    // Any click also counts as the gesture browsers require before audio.
    void this.alarm.unlock();
    this.acknowledging.set(request.id);
    try {
      await this.requests.acknowledge(request.id);
      await this.requests.resolve(request.id);
    } finally {
      this.acknowledging.set(null);
    }
  }

  /** Opens the Set PIN dialog for a `pin_reset` request; the dialog acknowledges + resolves the request itself once the PIN is set. */
  openSetPin(request: GuestRequest): void {
    void this.alarm.unlock();
    this.dialog.open<boolean, RewardPinResetDialogData>(RewardPinResetDialog, {
      width: '360px',
      data: {
        requestId: request.id,
        tableLabel: this.tableLabel(request),
        guestName: request.guestName || null,
        guestPhone: request.guestPhone,
      },
    });
  }

  /**
   * QA round 2, item I.1 — the lifecycle's last step, reachable from this
   * dock too (previously only Table Requests could reach it). No alarm
   * unlock/sound here on purpose: Done is staff clearing their own already-
   * handled item, not a new event that needs announcing.
   */
  async markDone(request: GuestRequest): Promise<void> {
    this.markingDone.set(request.id);
    try {
      await this.requests.resolve(request.id);
    } finally {
      this.markingDone.set(null);
    }
  }

  /**
   * "Water" requests get their own action: add the bottle to the table's
   * order (so it reaches the bill) and acknowledge in one tap. Falls back to
   * a plain acknowledge, with a nudge, when there's no water item on the menu.
   */
  async addWater(request: GuestRequest): Promise<void> {
    void this.alarm.unlock();
    this.acknowledging.set(request.id);
    try {
      const outcome = await this.requests.fulfilWaterRequest(request);
      // Same one-tap close-out as acknowledge() above — fulfilWaterRequest
      // only takes the request as far as 'acknowledged', which would have
      // left it sitting in the dock's second list waiting for a Mark done
      // it no longer offers.
      await this.requests.resolve(request.id);
      const message =
        outcome === 'ordered'
          ? `Water added to ${this.tableLabel(request)}.`
          : outcome === 'queued'
            ? `You're offline — the water order for ${this.tableLabel(request)} will send once you're back online.`
            : 'Acknowledged. Add a water item to your menu so it can be billed automatically.';
      this.toasts.open(message, 'Dismiss', { duration: 5000 });
    } catch (err) {
      // A stale permission set (canAddWater above is based on a token that
      // might not have refreshed yet) still gets a real explanation rather
      // than the generic fallback.
      const code = (err as { code?: string })?.code;
      const message =
        code === 'permission-denied' || code === 'functions/permission-denied'
          ? "You don't have permission to add orders — ask a manager to add the water."
          : err instanceof Error
            ? err.message
            : 'Could not add water to the order.';
      this.toasts.open(message, 'Dismiss', { duration: 4000 });
    } finally {
      this.acknowledging.set(null);
    }
  }

  /** Re-entrant guard: a trigger that arrives mid-round just asks the in-flight run to loop again rather than starting a second, overlapping one. */
  private kick(): void {
    if (this.busy) {
      this.dirty = true;
      return;
    }
    this.busy = true;
    void this.loop();
  }

  private async loop(): Promise<void> {
    try {
      do {
        this.dirty = false;
        await this.runOnce();
      } while (this.dirty);
    } catch (err) {
      // Speech/audio APIs vary a lot across browsers and devices; a failure
      // anywhere in one run must never leave `busy` stuck true — that would
      // silently wedge every future round (including the continuous warning
      // for the owner) for the rest of the tab's life, with nothing in the
      // UI to explain why the tune stopped firing while the pending-count
      // badge kept updating normally. Logged rather than swallowed
      // outright, so a real recurring failure is diagnosable from the
      // console instead of just "the alarm stopped for no reason".
      console.error('RequestAlerts: alert round failed, will retry on the next trigger', err);
    } finally {
      this.busy = false;
    }
  }

  private async runOnce(): Promise<void> {
    // Round 4, §8b — Settings → Notifications' "Table assistance request"
    // rule. Off means no sound/speech for this alert type, same as
    // treating the queue as empty for alerting purposes — the visible
    // request list itself (rendered straight off `pending()` in the
    // template) is untouched either way; this only gates the audible half.
    if (!this.entitlements.notificationRules().tableAssistance) {
      this.alarm.stop();
      this.alarm.endRound();
      return;
    }

    const pending = this.pending();

    if (pending.length === 0) {
      this.alarm.stop();
      this.alarm.endRound();
      this.announcedFor.clear();
      this.lastFiredRound = -1;
      this.continuousActive = false;
      this.continuousSpeechAt = 0;
      return;
    }

    const ids = pending.map((request) => request.id);
    const hasNew = ids.some((id) => !this.seen.has(id));
    this.seen = new Set(ids);
    // Auto-opening the dock on a new arrival is now RequestsTasksDock's own
    // job (it watches this same PendingRequestsService independently) —
    // this component no longer owns any expand/collapse state itself.

    const oldestMs = this.oldestPendingAt(pending);
    const round = oldestMs === null ? 0 : Math.max(0, Math.floor((this.now() - oldestMs) / ROUND_MS));
    const isNewRound = round !== this.lastFiredRound;

    if (round >= CONTINUOUS_AT_ROUND && (this.tenancy.isAdmin() || this.tenancy.role() === 'manager')) {
      // Continuous mode runs on its own clock from here — every tick (not
      // just round boundaries, a full minute apart) re-arms the tune
      // (AlarmService.start()'s doc comment on why a silently-paused one
      // would otherwise go unnoticed) and speaks again once
      // CONTINUOUS_SPEECH_MS has passed, so an admin is never more than 30
      // seconds from a reminder during the most urgent phase.
      if (!this.continuousActive) {
        this.continuousActive = true;
        this.continuousSpeechAt = 0; // speak immediately on entry
      }
      this.lastFiredRound = round;
      this.alarm.start();
      const due = this.now() - this.continuousSpeechAt >= CONTINUOUS_SPEECH_MS;
      if (hasNew || due) {
        this.continuousSpeechAt = this.now();
        await this.speakContinuous(pending);
      }
      return;
    }

    // Nothing changed since the last run: no new arrival, and still the
    // same round as last time this fired. Most ticks land here.
    if (!hasNew && !isNewRound) return;
    this.lastFiredRound = round;
    // Minute 1 is skipped by design — only a brand-new arrival sounds then.
    if (!hasNew && SILENT_ROUNDS.has(round)) return;

    // A non-admin viewer at round 3+, or anyone at round 0–2: an ordinary
    // bounded cycle. If continuous mode was active for an admin who then
    // signed out/impersonation ended mid-session, this also correctly
    // winds it back down to bounded cycles rather than leaving it stuck on.
    if (this.continuousActive) {
      this.continuousActive = false;
      this.alarm.stop();
    }
    this.alarm.playRound();
    try {
      await this.speakFor(pending, hasNew);
    } finally {
      this.alarm.endRound();
    }
  }

  /**
   * Continuous mode's own announcement — deliberately not per-request:
   * several requests all past three minutes unaddressed would otherwise
   * queue up as separate utterances that overlap or talk over each other
   * every 30 seconds. One table gets named (there's nothing to
   * disambiguate); more than one collapses to a single generic line.
   */
  private async speakContinuous(pending: GuestRequest[]): Promise<void> {
    if (this.alarm.muted() || !this.speech.supported) return;
    this.alarm.duck();
    try {
      const message =
        pending.length > 1
          ? 'Unattended requests. Please acknowledge immediately.'
          : `${this.tableLabel(pending[0])} is still waiting. Please acknowledge immediately.`;
      await this.speech.announce(message);
    } finally {
      this.alarm.unduck();
    }
  }

  private async speakFor(pending: GuestRequest[], hasNew: boolean): Promise<void> {
    if (this.alarm.muted() || !this.speech.supported) {
      // Muted staff get no speech, but the ids are still burned: unmuting
      // should not replay a backlog of what happened while it was off.
      this.announcedFor = new Set(pending.map((request) => request.id));
      return;
    }
    // The words are the point — duck the tune under the announcement so
    // it doesn't have to compete with itself, then restore it once the
    // speech is done (endRound's fade takes it from there).
    this.alarm.duck();
    try {
      if (hasNew) {
        await this.announceNewArrivals(pending);
      } else {
        await this.announceStillWaiting(pending);
      }
    } finally {
      this.alarm.unduck();
    }
  }

  private oldestPendingAt(pending: GuestRequest[]): number | null {
    let oldest: number | null = null;
    for (const request of pending) {
      const at = toMillis(request.createdAt);
      if (at !== null && (oldest === null || at < oldest)) oldest = at;
    }
    return oldest;
  }

  /**
   * Speaks each newly arrived request once — "Water bottle for table 16" —
   * over the top of the round's tune. Reads allTables() so that when the
   * table list arrives this gets another chance: announcing before it
   * lands would say "for table" with no number. Nothing is marked
   * announced until it has actually been handed to the speech engine, so a
   * deferred request is picked up on that later pass.
   */
  private async announceNewArrivals(pending: GuestRequest[]): Promise<void> {
    const tables = this.allTables();
    const fresh = pending.filter((request) => !this.announcedFor.has(request.id));

    // A venue with no tables can't have table requests, so an empty list
    // here means the listener hasn't delivered yet.
    if (fresh.length > 0 && tables.length === 0) return;

    const spoken = fresh.slice(0, MAX_SPOKEN_PER_BATCH);
    for (const request of spoken) await this.speech.announce(this.announcement(request, tables));

    const remaining = fresh.length - spoken.length;
    if (remaining > 0) {
      await this.speech.announce(
        remaining === 1 ? 'And one more table waiting' : `And ${remaining} more tables waiting`
      );
    }

    // Rebuilt from what's still pending rather than added to, so the set
    // can't grow for the life of the tab.
    this.announcedFor = new Set(pending.map((request) => request.id));
  }

  /** A round boundary with nothing new — repeats the oldest complaint rather than the whole backlog, so the message doesn't grow every minute the floor stays behind. */
  private async announceStillWaiting(pending: GuestRequest[]): Promise<void> {
    const oldest = [...pending].sort(
      (a, b) => (toMillis(a.createdAt) ?? 0) - (toMillis(b.createdAt) ?? 0)
    )[0];
    if (!oldest) return;
    const label = this.tableLabel(oldest);
    const extra = pending.length > 1 ? ` and ${pending.length - 1} more` : '';
    await this.speech.announce(`${label} is still waiting${extra}`);
  }

  private announcement(request: GuestRequest, tables: VenueTable[]): string {
    const reason = REQUEST_REASON_SPEECH[request.reason] ?? REQUEST_REASON_SPEECH.custom;
    const table = tables.find((candidate) => candidate.id === request.tableId);
    return table ? `${reason} for table ${table.number}` : `${reason} for a table`;
  }
}
